import { computed, nextTick, ref, type Ref } from 'vue'

import { useSourceToast } from '~/composables/ui/useSourceToast'

export type FormValidationFailureKind = 'client' | 'api'

export type UseFormValidationFeedbackOptions = {
  /** Ordre des champs pour le focus et le résumé toast. */
  fieldOrder?: string[]
  /** Noms API/Vine → clés UI (ex. `{ fct: 'fonction' }`). */
  fieldMap?: Record<string, string>
}

type VineMessageItem = { field?: string; message?: string }

type FieldMap = Record<string, string>

/** Forme ApiValidationError (duck-typing, sans import du module errors). */
type ApiValidationLike = {
  name: string
  fieldErrors: Record<string, string[]>
}

function isApiValidationError(error: unknown): error is ApiValidationLike {
  if (!error || typeof error !== 'object') return false
  const candidate = error as { name?: unknown; fieldErrors?: unknown }
  if (candidate.name !== 'ApiValidationError') return false
  return !!candidate.fieldErrors && typeof candidate.fieldErrors === 'object' && !Array.isArray(candidate.fieldErrors)
}

function isVineValidationError(error: unknown): error is { messages: VineMessageItem[] } {
  if (!error || typeof error !== 'object') return false
  const messages = (error as { messages?: unknown }).messages
  return Array.isArray(messages)
}

function firstMessage(messages: string[] | undefined): string {
  const text = messages?.find((item) => typeof item === 'string' && item.trim().length > 0)?.trim()
  return text ?? ''
}

function mapFieldName(rawField: string, fieldMap: FieldMap): string {
  const trimmed = rawField.trim()
  if (!trimmed) return trimmed
  return fieldMap[trimmed] ?? trimmed
}

function omitFieldError(errors: Record<string, string>, mapped: string): Record<string, string> {
  const { [mapped]: _removed, ...rest } = errors
  return rest
}

function mergeFieldErrorRecord(
  current: Record<string, string>,
  record: Record<string, string | string[]>,
  fieldMap: FieldMap,
): Record<string, string> {
  const next: Record<string, string> = { ...current }
  for (const [rawField, rawMessages] of Object.entries(record)) {
    const mapped = mapFieldName(rawField, fieldMap)
    if (!mapped) continue
    const message = Array.isArray(rawMessages)
      ? firstMessage(rawMessages)
      : String(rawMessages ?? '').trim()
    if (!message) continue
    next[mapped] = message
  }
  return next
}

function vineMessagesToRecord(messages: VineMessageItem[]): Record<string, string[]> {
  const record: Record<string, string[]> = {}
  for (const item of messages) {
    const field = typeof item.field === 'string' ? item.field : ''
    const message = typeof item.message === 'string' ? item.message.trim() : ''
    if (!field || !message) continue
    if (!record[field]) record[field] = []
    record[field].push(message)
  }
  return record
}

function applyApiValidationError(
  fieldErrors: Ref<Record<string, string>>,
  error: ApiValidationLike,
  fieldMap: FieldMap,
): boolean {
  fieldErrors.value = mergeFieldErrorRecord(fieldErrors.value, error.fieldErrors, fieldMap)
  return Object.keys(fieldErrors.value).length > 0
}

function applyVineMessages(
  fieldErrors: Ref<Record<string, string>>,
  messages: VineMessageItem[],
  fieldMap: FieldMap,
): boolean {
  fieldErrors.value = mergeFieldErrorRecord(
    fieldErrors.value,
    vineMessagesToRecord(messages),
    fieldMap,
  )
  return Object.keys(fieldErrors.value).length > 0
}

function applyFromUnknownError(
  fieldErrors: Ref<Record<string, string>>,
  error: unknown,
  fieldMap: FieldMap,
): boolean {
  if (isApiValidationError(error)) {
    return applyApiValidationError(fieldErrors, error, fieldMap)
  }
  if (isVineValidationError(error)) {
    return applyVineMessages(fieldErrors, error.messages, fieldMap)
  }
  return false
}

function summaryDescription(fieldErrors: Record<string, string>): string {
  const count = Object.keys(fieldErrors).length
  if (count <= 0) return 'Veuillez corriger le formulaire.'
  if (count === 1) {
    const only = Object.values(fieldErrors)[0]
    return only || 'Corrigez le champ en erreur.'
  }
  return `Corrigez les ${count} champs en erreur.`
}

function resolveFirstInvalidField(
  fieldErrors: Record<string, string>,
  fieldOrder: string[],
): string | null {
  for (const field of fieldOrder) {
    if (fieldErrors[field]) return field
  }
  const keys = Object.keys(fieldErrors)
  return keys[0] ?? null
}

function focusFirstInvalidField(field: string | null): void {
  if (!field || !import.meta.client) return
  void nextTick(() => {
    const byName = document.querySelector<HTMLElement>(
      `[name="${CSS.escape(field)}"], #${CSS.escape(field)}`,
    )
    if (byName) {
      byName.focus()
      return
    }
    const invalid = document.querySelector<HTMLElement>('[aria-invalid="true"]')
    invalid?.focus()
  })
}

/**
 * Système unique d’erreurs de formulaire : fieldErrors + toast de synthèse.
 * Les forms ne gardent en local que les règles métier via `setFieldError`.
 */
export function useFormValidationFeedback(options: UseFormValidationFeedbackOptions = {}) {
  const toast = useSourceToast()
  const fieldErrors = ref<Record<string, string>>({}) as Ref<Record<string, string>>
  const fieldOrder = options.fieldOrder ?? []
  const fieldMap = options.fieldMap ?? {}

  const hasFieldErrors = computed(() => Object.keys(fieldErrors.value).length > 0)
  const fieldErrorCount = computed(() => Object.keys(fieldErrors.value).length)

  function fieldError(name: string): string {
    return fieldErrors.value[name] ?? ''
  }

  function setFieldError(field: string, message: string): void {
    const mapped = mapFieldName(field, fieldMap)
    const text = message.trim()
    if (!mapped || !text) return
    fieldErrors.value = { ...fieldErrors.value, [mapped]: text }
  }

  function clearFieldError(field: string): void {
    const mapped = mapFieldName(field, fieldMap)
    if (!mapped || !(mapped in fieldErrors.value)) return
    fieldErrors.value = omitFieldError(fieldErrors.value, mapped)
  }

  function clearAll(): void {
    fieldErrors.value = {}
  }

  function applyFieldErrorRecord(record: Record<string, string | string[]>): void {
    fieldErrors.value = mergeFieldErrorRecord(fieldErrors.value, record, fieldMap)
  }

  function applyFromUnknown(error: unknown): boolean {
    return applyFromUnknownError(fieldErrors, error, fieldMap)
  }

  function notifySubmitFailure(kind: FormValidationFailureKind): void {
    const title = kind === 'client' ? 'Formulaire invalide' : 'Enregistrement impossible'
    toast.error(title, summaryDescription(fieldErrors.value))
  }

  function notifyNonFieldError(title: string, message: string): void {
    toast.error(title, message.trim() || 'Une erreur est survenue.')
  }

  function firstInvalidField(): string | null {
    return resolveFirstInvalidField(fieldErrors.value, fieldOrder)
  }

  function focusFirstInvalid(): void {
    focusFirstInvalidField(firstInvalidField())
  }

  return {
    fieldErrors,
    hasFieldErrors,
    fieldErrorCount,
    fieldError,
    setFieldError,
    clearFieldError,
    clearAll,
    applyFieldErrorRecord,
    applyFromUnknown,
    notifySubmitFailure,
    notifyNonFieldError,
    firstInvalidField,
    focusFirstInvalid,
  }
}

export type FormValidationFeedback = ReturnType<typeof useFormValidationFeedback>
